import { useState, FormEvent } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/SEOHead';

export default function ContactPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact Fresno Electrical Services',
    description: 'Get in touch with our licensed electricians in Fresno, CA for quotes, emergency services, or general inquiries',
    url: `${siteUrl}/contact`,
    mainEntity: {
      '@type': 'ElectricalContractor',
      name: 'Fresno Electrical Services',
      email: 'info@fresnoelectrical.com',
      address: {
        '@type': 'PostalAddress',
        streetAddress: '123 Main Street',
        addressLocality: 'Fresno',
        addressRegion: 'CA',
        postalCode: '93721',
        addressCountry: 'US'
      }
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    urgency: '',
    message: ''
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const SLACK_WEBHOOK_URL = 'YOUR_SLACK_WEBHOOK_URL';

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setStatus('sending');

    const formBody = new URLSearchParams();
    formBody.append('name', formData.name);
    formBody.append('email', formData.email);
    formBody.append('phone', formData.phone);
    formBody.append('urgency', formData.urgency);
    formBody.append('message', formData.message);

    const slackPayload = {
      text: `📋 *New Quote Request — Contact Page*\n\n*Name:* ${formData.name}\n*Email:* ${formData.email}\n*Phone:* ${formData.phone}\n*Urgency:* ${formData.urgency}\n*Message:* ${formData.message}`,
    };

    try {
      const [readyResponse] = await Promise.allSettled([
        fetch('https://readdy.ai/api/form/d6ontcl39lnhn4hh1e30', {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: formBody.toString(),
        }),
        fetch(SLACK_WEBHOOK_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(slackPayload),
        }),
      ]);

      if (readyResponse.status === 'fulfilled' && readyResponse.value.ok) {
        setStatus('success');
        setFormData({ name: '', email: '', phone: '', urgency: '', message: '' });
        setTimeout(() => setStatus('idle'), 5000);
      } else {
        setStatus('error');
        setTimeout(() => setStatus('idle'), 5000);
      }
    } catch (error) {
      setStatus('error');
      setTimeout(() => setStatus('idle'), 5000);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead schema={schema} />
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-[#1a1a1a] text-white pt-32 pb-20">
        <div className="absolute inset-0 bg-gradient-to-br from-black/50 via-transparent to-[#D4AF37]/10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full px-6 py-2 mb-6">
              <i className="ri-customer-service-2-line text-[#D4AF37] text-xl"></i>
              <span className="text-[#D4AF37] font-medium text-sm">24/7 Available</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Get In Touch</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Have a question or need electrical services? We're here to help. Reach out to our expert team today.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-mail-line text-[#D4AF37] text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Email Us</h3>
              <p className="text-gray-600 text-sm mb-3">Quick response</p>
              <a href="mailto:info@electriciansolutionsfresno.com" className="text-[#D4AF37] font-semibold hover:underline break-all text-sm">
                info@electriciansolutionsfresno.com
              </a>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-map-pin-line text-[#D4AF37] text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Visit Us</h3>
              <p className="text-gray-600 text-sm mb-3">Main Office</p>
              <p className="text-gray-700 font-medium">
                123 Electrical Ave<br />
                Fresno, CA 93721
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm hover:shadow-md transition-shadow border border-gray-100">
              <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mb-4">
                <i className="ri-time-line text-[#D4AF37] text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Hours</h3>
              <p className="text-gray-600 text-sm mb-3">Always Available</p>
              <p className="text-gray-700 font-medium">
                24/7 Emergency<br />
                Service Available
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
            {/* Form */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Get a Free Quote</h2>
                <p className="text-gray-600 text-lg">
                  Fill out the form below and we'll get back to you as soon as possible.
                </p>
              </div>

              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-900 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent outline-none transition-all text-sm"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-900 mb-2">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent outline-none transition-all text-sm"
                      placeholder="(559) 555-1234"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent outline-none transition-all text-sm"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="urgency" className="block text-sm font-semibold text-gray-900 mb-2">
                    Urgency *
                  </label>
                  <select
                    id="urgency"
                    name="urgency"
                    required
                    value={formData.urgency}
                    onChange={(e) => setFormData({ ...formData, urgency: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent outline-none transition-all text-sm bg-white cursor-pointer"
                  >
                    <option value="">Select urgency level...</option>
                    <option value="Emergency — Need help now">🚨 Emergency — Need help now</option>
                    <option value="Urgent — Within 24 hours">⚡ Urgent — Within 24 hours</option>
                    <option value="Soon — Within a few days">📅 Soon — Within a few days</option>
                    <option value="Planning — Just getting a quote">💬 Planning — Just getting a quote</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-900 mb-2">
                    Message *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent outline-none transition-all resize-none text-sm"
                    placeholder="Tell us about your project..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full bg-[#D4AF37] text-black font-bold py-4 px-8 rounded-lg hover:bg-[#C5A028] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 whitespace-nowrap"
                >
                  {status === 'sending' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin text-xl"></i>
                      Sending...
                    </>
                  ) : status === 'success' ? (
                    <>
                      <i className="ri-check-line text-xl"></i>
                      Quote Sent!
                    </>
                  ) : status === 'error' ? (
                    <>
                      <i className="ri-error-warning-line text-xl"></i>
                      Failed - Try Again
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-fill text-xl"></i>
                      Get Free Quote
                    </>
                  )}
                </button>

                {status === 'success' && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
                    <i className="ri-checkbox-circle-fill text-green-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-green-800 font-semibold text-sm">Thank you for contacting us!</p>
                      <p className="text-green-700 text-sm mt-1">We'll get back to you within 24 hours.</p>
                    </div>
                  </div>
                )}

                {status === 'error' && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                    <i className="ri-error-warning-fill text-red-600 text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-red-800 font-semibold text-sm">Something went wrong</p>
                      <p className="text-red-700 text-sm mt-1">Please try again or email us directly.</p>
                    </div>
                  </div>
                )}
              </form>
            </div>

            {/* Map */}
            <div>
              <div className="mb-8">
                <h2 className="text-4xl font-bold text-gray-900 mb-4">Find Us</h2>
                <p className="text-gray-600 text-lg">
                  Located in the heart of Fresno, serving all of Central California.
                </p>
              </div>

              <div className="rounded-xl overflow-hidden shadow-lg border border-gray-200 h-[500px]">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d102573.08469724!2d-119.87195!3d36.746842!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x80945de1549e4e9d%3A0x7b12406449a3b811!2sFresno%2C%20CA!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Electrician Solutions Fresno Location"
                ></iframe>
              </div>

              <div className="mt-8 bg-[#1a1a1a] rounded-xl p-8 text-white">
                <h3 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <i className="ri-flashlight-fill text-[#D4AF37]"></i>
                  Emergency Service
                </h3>
                <p className="text-gray-300 mb-4">
                  Need immediate assistance? We offer 24/7 emergency electrical services throughout Fresno and surrounding areas. Fill out the quote form and select "Emergency" for fastest response.
                </p>
                <a
                  href="mailto:info@electriciansolutionsfresno.com"
                  className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-bold py-3 px-6 rounded-lg hover:bg-[#C5A028] transition-all whitespace-nowrap"
                >
                  <i className="ri-mail-fill"></i>
                  Email Us Now
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-16 bg-gray-50 border-t border-gray-200">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <h3 className="text-center text-sm font-bold text-gray-500 uppercase tracking-wider mb-8">
              Licensed, Certified & Trusted
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 items-center">
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <i className="ri-shield-check-fill text-[#D4AF37] text-3xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 text-center">Licensed & Insured</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <i className="ri-award-fill text-[#D4AF37] text-3xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 text-center">Certified Electricians</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <i className="ri-time-fill text-[#D4AF37] text-3xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 text-center">24/7 Availability</p>
              </div>
              <div className="flex flex-col items-center gap-3">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center shadow-sm">
                  <i className="ri-star-fill text-[#D4AF37] text-3xl"></i>
                </div>
                <p className="text-sm font-semibold text-gray-700 text-center">5-Star Rated</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
